import { LocalStorageKey, ROYCO_API_URL, ROYCO_MARKET_ACTION_URL } from '@/constants/shared';
import { TUserExperienceMeta } from '@/types';
import { trimSlashes } from '@/utils';
import { readContracts } from '@wagmi/core';
import { ActionControllerRecipeApMarketActionData } from 'royco/api';
import { erc20Abi } from 'viem';
import { Config } from 'wagmi';

export const createUrl = (endpoint: string) => import.meta.env.VITE_HUB_API_URL + trimSlashes(endpoint);

export const makeRequest = (endpoint: string, options: RequestInit = {}) => {
  const deviceId = localStorage.getItem(LocalStorageKey.PlumeDeviceId) ?? '';
  return fetch(createUrl(endpoint), {
    ...options,
    // TODO: eventually, we may want to allow request creation outside of the makeRequest function
    headers: { ...options.headers, 'Content-Type': 'application/json', 'x-device-id': deviceId },
    credentials: 'include',
  });
};

// TODO: move this into the royco client
//       The parameters to pass in body are:
// id: global market id (<CHAIN_ID_<MARKET_TYPE>_<MARKET_ID>)
// (Ex: 146_0_0xc2b6fade2c202f102b59c4f2a1dabcf414deec4bd0b868757e218ed19f3c8ca6)
// quantity: amount to deposit in wei (in string) (Ex: 100000000)
export const makeRoycoMarketInteractionRequest = async ({
  chainId,
  marketId,
  amount,
  accountAddress,
}: {
  chainId: string;
  marketId: string;
  amount: string;
  accountAddress: string;
}): Promise<ActionControllerRecipeApMarketActionData> => {
  try {
    const res = await fetch(ROYCO_MARKET_ACTION_URL, {
      method: 'POST',
      headers: { 'x-api-key': import.meta.env.VITE_ROYCO_API_KEY, 'content-type': 'application/json' },
      body: JSON.stringify({
        id: `${chainId}_0_${marketId}`,
        quantity: amount,
        accountAddress,
      }),
    });
    const resJson = await res.json();
    return resJson;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const makeRoycoPositionsRequest = async ({
  chainId,
  marketId,
  accountAddress,
}: {
  chainId: string;
  marketId: string;
  accountAddress: string;
}) => {
  try {
    const res = await fetch(`${ROYCO_API_URL}/position/recipe/${chainId}_0_${marketId}/${accountAddress}`, {
      headers: { 'x-api-key': import.meta.env.VITE_ROYCO_API_KEY, 'content-type': 'application/json' },
    });
    const resJson = await res.json();
    return resJson;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const sendSetUserExperienceMetadataRequest = async (userExperienceMeta: TUserExperienceMeta) => {
  return await makeRequest('/user/userExperienceMeta', {
    method: 'PUT',
    body: JSON.stringify(userExperienceMeta),
  });
};

/**
 * This is a util function to test onboarding flow end to end
 */
export const sendSetHasCompletedOnboardingFalseRequest = async () => {
  await makeRequest('/user/onboarding/completion', {
    method: 'POST',
    body: JSON.stringify({
      hasCompletedOnboarding: false,
    }),
  });
};

/**
 * This is a util function to test home screen banners
 */

export const clearBannerState = async () => {
  const userMeta: TUserExperienceMeta = {
    dismissedBanners: {},
    bannerExpirations: {},
  };
  await sendSetUserExperienceMetadataRequest(userMeta);
};

export const getTokenSymbolsFromAddressList = async (tokenAddressList: string[], wagmiConfig: Config) => {
  const symbolCalls = tokenAddressList.map((address) => ({
    address: address as `0x${string}`,
    abi: erc20Abi,
    functionName: 'symbol',
  }));

  const results = await readContracts(wagmiConfig, { contracts: symbolCalls });

  return results
    .map((result, idx) => {
      if (result.status !== 'success') {
        console.error('Failed to read symbol', result);
        return null;
      }
      return { tokenSymbol: result.result as string, tokenAddress: tokenAddressList[idx] };
    })
    .filter((x) => x != null);
};

export const checkGeoCompliance = async (): Promise<{ restrictAccess: boolean }> => {
  try {
    const res = await makeRequest('auth/geoCompliance');
    const resJson = await res.json();
    return resJson;
  } catch (e) {
    console.error(e);
    // if we have an error, allow the user to pass through for now
    // the endpoint will return a 404 until we deploy backend
    return { restrictAccess: false };
  }
};

/**
 * This is a testing utility to force Battle Group win for the current user
 */
export const forceBattleGroupWin = async () => {
  return await makeRequest('/dev/bg/force-win', {
    method: 'POST',
  });
};

/**
 * This is a testing utility to clear Battle Group win for the current user
 */
export const clearBattleGroupWin = async () => {
  return await makeRequest('/dev/bg/clear-win', {
    method: 'POST',
  });
};
